import React from 'react';
import Container from './Container';
import LocationCardSkeleton from './LocationCardSkeleton';

/**
 * LocationPageSkeleton component for displaying a loading state for location detail pages
 * 
 * @returns {JSX.Element} - LocationPageSkeleton component
 */
const LocationPageSkeleton = () => {
  return (
    <>
      {/* Tan background header section with skeleton */}
      <div className="bg-brand-cream pt-32 pb-32">
        <Container>
          <div className="text-left px-6">
            <div className="flex items-center mb-3 animate-pulse">
              <div className="h-3 bg-gray-300 w-12"></div>
              <div className="mx-2 h-3 bg-gray-300 w-2"></div>
              <div className="h-3 bg-gray-300 w-20"></div>
              <div className="mx-2 h-3 bg-gray-300 w-2"></div>
              <div className="h-3 bg-gray-300 w-16"></div>
            </div>
            <div className="h-10 bg-gray-300 w-64 animate-pulse"></div>
          </div>
        </Container>
      </div>

      {/* Main content with overlapping image skeleton */}
      <div className="bg-white">
        <Container className="-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - About Content Skeleton */}
            <div className="bg-white rounded-lg p-6 px-6 animate-pulse">
              <div className="h-7 bg-gray-200 w-48 mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 w-full"></div>
                <div className="h-4 bg-gray-200 w-11/12"></div>
                <div className="h-4 bg-gray-200 w-full"></div>
                <div className="h-4 bg-gray-200 w-10/12"></div>
                <div className="h-4 bg-gray-200 w-full"></div>
                <div className="h-4 bg-gray-200 w-9/12"></div>
              </div>

              {/* Action Buttons Skeleton */}
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="h-10 bg-gray-200 w-32 rounded"></div>
                <div className="h-10 bg-gray-200 w-32 rounded"></div>
              </div>
            </div>

            {/* Right Column - Image Skeleton */}
            <div className="lg:-mt-24">
              <div className="w-full h-[500px] bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </Container>
      </div>

      {/* Map Section with Side Card Skeleton */}
      <div className="relative py-16 pb-32 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {/* Map Skeleton - Takes 2/3 of the width */}
          <div className="lg:col-span-2 h-[600px] bg-gray-200 animate-pulse"></div>
          
          {/* Contact Card Skeleton - Takes 1/3 of the width */}
          <div className="lg:col-span-1 bg-gray-100 flex items-center">
            <div className="p-8 w-full animate-pulse">
              {/* Where to Find Us Skeleton */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="h-6 w-6 bg-gray-300 rounded-full mr-3"></div>
                  <div className="h-5 bg-gray-300 w-40"></div>
                </div>
                <div className="ml-9 space-y-2">
                  <div className="h-4 bg-gray-200 w-48"></div>
                  <div className="h-4 bg-gray-200 w-32"></div>
                </div>
              </div>

              {/* How to Reach Us Skeleton */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="h-6 w-6 bg-gray-300 rounded-full mr-3"></div>
                  <div className="h-5 bg-gray-300 w-40"></div>
                </div>
                <div className="ml-9">
                  <div className="h-4 bg-gray-200 w-32"></div>
                </div>
              </div>

              {/* Hours of Operation Skeleton */}
              <div>
                <div className="flex items-center mb-4">
                  <div className="h-6 w-6 bg-gray-300 rounded-full mr-3"></div>
                  <div className="h-5 bg-gray-300 w-40"></div>
                </div>
                <div className="ml-9 space-y-2">
                  {[...Array(7)].map((_, index) => (
                    <div key={index} className="flex justify-between">
                      <div className="h-3 bg-gray-200 w-16"></div>
                      <div className="h-3 bg-gray-200 w-24 ml-4"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nearby Locations Skeleton */}
      <div className="bg-white py-16">
        <Container>
          <div className="mb-6 flex justify-between items-center">
            <div className="h-7 bg-gray-200 w-56 animate-pulse"></div>
            <div className="h-5 bg-gray-200 w-32 animate-pulse"></div>
          </div>

          {/* Nearby Locations Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <LocationCardSkeleton key={index} />
            ))}
          </div>
        </Container>
      </div>
    </>
  );
};

export default LocationPageSkeleton;
